import React, {useState} from "react";
import {Container, FormControl, InputLabel, Select, Typography} from "@material-ui/core";
import {Field, Form, Formik, useFormikContext} from "formik";
import { OutlinedInput } from '@material-ui/core';
import {Autocomplete, Box, Button, TextField} from "@mui/material";
import InfoIcon from "@material-ui/icons/Info";
import MenuItem from "@material-ui/core/MenuItem";
import {REGIONS} from "../../../../utils/META/cat_101";
import {PROGRAM_TARGETS} from "../../../../utils/META/cat_108";
import {PROGRAM_VENUE} from "../../../../utils/META/cat_111";
import {PROGRAM_DURATION} from "../../../../utils/META/cat_112";
import {RESOURCES_TYPE} from "../../../../utils/META/cat_114";
import {PROGRAM_STATUS} from "../../../../utils/META/cat_121";
import {PROGRAM_COST} from "../../../../utils/META/cat_125";
import useStyles from "../../../../assets/styles/ProgramRatingStyles";
import {AGENDA_TOPIC} from "../../../../utils/META/cat_123";
import {RESOURCES_FORMAT} from "../../../../utils/META/cat_115";
import {TARGET_AGE} from "../../../../utils/META/cat_109";
import {RESOURCE_TYPE} from "../../../../utils/META/cat_122";
import {LANGUAGES} from "../../../../utils/META/cat_114(new)";

function validateWordLength(value, max_length) {
    const length = value ? value.match(/\w+/g).length : 0;
    const error = length > max_length;
    const error_message = error ? 'Word limit exceeded' : undefined;
    return { length, max_length, error, error_message };
}

function MaxLengthHelperText({ length, max_length, error, error_message }) {
    return <Box display={'flex'} flexDirection={'row'}>
        {error && <Box mr={'auto'}>{error_message}</Box>}
        <Box ml={'auto'}>{length}/{max_length}</Box>
    </Box>
}

const FormTextField = ({ label, name, required = true, multiline = false, maxRows, helperText, error, validate, ...rest }) => {
    const [isFocused, setIsFocused] = useState(false);
    const classes = useStyles();

    return (
        <Field name={name} validate={validate}>
            {({ field, form }) => (
                <FormControl variant="outlined" className={classes.textFieldRoot} fullWidth>
                    <InputLabel htmlFor={name} shrink={isFocused || field.value}>
                        {label}
                    </InputLabel>
                    <OutlinedInput
                        {...field}
                        id={name}
                        label={label}
                        fullWidth
                        required={required}
                        multiline={multiline}
                        rowsMax={maxRows}
                        onFocus={() => setIsFocused(true)}
                        onBlur={(e) => {
                            setIsFocused(false);
                            field.onBlur(e);
                        }}
                        error={error}
                        aria-describedby={`${name}-helper-text`}
                        {...rest}
                    />
                    {helperText && (
                        <div id={`${name}-helper-text`} style={{ color: error ? 'red' : 'inherit' }}>
                            {helperText}
                        </div>
                    )}
                </FormControl>
            )}
        </Field>
    );
};


const FormSelect = ({ label, name, options }) => (
    <FormControl fullWidth variant="outlined" margin="normal" required style={{marginBottom:'15px',backgroundColor:'#fff'}}>
        <InputLabel htmlFor={name}>{label}</InputLabel>
        <Field as={Select} label={label} name={name}>
            {options.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </Field>
    </FormControl>
);

const FormComponent = ({ disableSubmit, setFieldValue }) => {
    const classes = useStyles();
    const { values } = useFormikContext();
    const aimsObjValidProps = validateWordLength(values.ProgramAims, 250);
    const descObjValidProps = validateWordLength(values.ProgramDesc, 250);
    const courseNameObjValidProps = validateWordLength(values.CourseName, 250);
    const courseLoObjValidProps = validateWordLength(values.CourseLearningObj, 250);
    const CourseDescObjValidProps = validateWordLength(values.CourseDesc, 250);
    const targetAudienceOptions = Object.values(PROGRAM_TARGETS);
    const courseTargetAudienceOptions = Object.values(PROGRAM_TARGETS);
    const targetAgeOptions = Object.values(TARGET_AGE);
    const courseTargetAgeOptions = Object.values(TARGET_AGE);
    const countriesOptions = Object.values(REGIONS);
    const programVenueOptions = Object.values(PROGRAM_VENUE);
    const resourcesTypeOptions = Object.values(RESOURCE_TYPE);
    const courseResourcesTypeOptions = Object.values(RESOURCE_TYPE);
    const programResourcesFormatOptions = Object.values(RESOURCES_FORMAT);
    const courseResourcesFormatOptions = Object.values(RESOURCES_FORMAT);
    const languagesOptions = Object.values(LANGUAGES);
    const [durationError, setDurationError] = useState(false);

    const handleValueCheck = (value) => {
        if (value && parseInt(value) > 20) {
            setDurationError(false);
        } else {
            setDurationError(true);
        }
    };

    function parseInitialValues(initialValue, options) {
        return initialValue
            ? initialValue.split(', ').map(value => options.find(option => option.value === value)).filter(Boolean)
            : [];
    }

    const targetAudienceInitialValues = parseInitialValues(values.ProgramTarget, targetAudienceOptions);
    const courseTargetAudienceInitialValues = parseInitialValues(values.CourseTarget, courseTargetAudienceOptions);
    const targetAgeInitialValues = parseInitialValues(values.TargetAge, targetAgeOptions);
    const CourseTargetAgeInitialValues = parseInitialValues(values.CourseTargetAge, courseTargetAgeOptions);
    const countriesInitialValues = parseInitialValues(values.TargetCountry, countriesOptions);
    const venueInitialValues = parseInitialValues(values.ProgramVenue, programVenueOptions);
    const resourcesTypeInitialValues = parseInitialValues(values.ResourcesType, resourcesTypeOptions);
    const courseResourcesTypeInitialValues = parseInitialValues(values.CourseResourcesType, courseResourcesTypeOptions);
    const programResourcesFormatInitialValues = parseInitialValues(values.ResourcesFormat, programResourcesFormatOptions);
    const courseResourcesFormatInitialValues = parseInitialValues(values.CourseResourcesFormat, courseResourcesFormatOptions);
    const languagesInitialValues = parseInitialValues(values.Languages, languagesOptions);

    const handleAutocompleteChange = (fieldName) => (event, value) => {
        const stringValue = value.map(item => item.value).join(', ');
        setFieldValue(fieldName, stringValue);
    };

    return (
        <>
        <Form>
            <div className={classes.section}>
                <Typography variant="h4" component="h1" gutterBottom className={classes.infoTitle}>
                    Program Information
                </Typography>
                <Typography className={classes.instruction} variant="body1"><InfoIcon/> The following information will
                    help us understand and evaluate your program more holistically .
                </Typography>
                <FormTextField label="Title of program" name="ProgramName" />
                {/*<FormTextField*/}
                {/*    label="Objectives of program"*/}
                {/*    name="ProgramAims"*/}
                {/*    multiline={true}*/}
                {/*    maxRows={10}*/}
                {/*    helperText={<MaxLengthHelperText {...aimsObjValidProps} />}*/}
                {/*    error={aimsObjValidProps.error}*/}
                {/*    validate={() => aimsObjValidProps.error_message}*/}
                {/*/>*/}
                <FormTextField
                    label="Description of program"
                    name="ProgramDesc"
                    multiline={true}
                    maxRows={10}
                    helperText={<MaxLengthHelperText {...descObjValidProps} />}
                    error={descObjValidProps.error}
                    validate={() => descObjValidProps.error_message}
                />
                {/*<FormTextField*/}
                {/*    label="Objectives of program"*/}
                {/*    name="ProgramAims"*/}
                {/*    multiline={true}*/}
                {/*    maxRows={10}*/}
                {/*    helperText={<MaxLengthHelperText {...aimsObjValidProps} />}*/}
                {/*    error={aimsObjValidProps.error}*/}
                {/*    validate={() => aimsObjValidProps.error_message}*/}
                {/*/>*/}
                <FormSelect
                    label="Agenda or topic your program addresses"
                    name="AgendaTopics"
                    options={AGENDA_TOPIC}
                />
                <FormSelect
                    label="Current status of program"
                    name="Status"
                    options={PROGRAM_STATUS}
                />
                <FormTextField label="Year of program launch" name="StartYear" type="number"/>
                <FormTextField label="Year of program last update" name="UpdateYear" type="number"/>
                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 1, marginBottom: 3}}
                    multiple
                    fullWidth={true}
                    name="ProgramTarget"
                    options={Object.values(PROGRAM_TARGETS)}
                    getOptionLabel={(option) => option.label}
                    value={targetAudienceInitialValues}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("ProgramTarget")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Target audience group (Select all that apply)"
                            InputLabelProps={ { required: true }}
                        />
                    )}
                />
                {values.ProgramTarget && (values.ProgramTarget.includes('Others') || (values.ProgramTarget.length > 1 && values.ProgramTarget.includes('Others'))) ? (
                    <FormTextField
                        label="Please specify target audience."
                        name="ProgramTargetOther"
                    />
                ) : null}

                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 2}}
                    multiple
                    fullWidth={true}
                    name="TargetAge"
                    options={Object.values(TARGET_AGE)}
                    value={targetAgeInitialValues}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("TargetAge")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Target audience age (Select all that apply)"
                            InputLabelProps={{required: true}}
                        />
                    )}
                />

                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 2, marginBottom: 2}}
                    multiple
                    fullWidth={true}
                    name="TargetCountry"
                    options={Object.values(REGIONS)}
                    value={countriesInitialValues}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("TargetCountry")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Target countries (Select all that apply)"
                            InputLabelProps={{required: true}}
                            // placeholder="Multiple Autocomplete"
                        />
                    )}
                />

                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 3, marginBottom: 1}}
                    multiple
                    fullWidth={true}
                    name="ProgramVenue"
                    options={Object.values(PROGRAM_VENUE)}
                    value={venueInitialValues}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("ProgramVenue")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Where is your program delivered? (Select all that apply)"
                            InputLabelProps={ { required: true }}
                        />
                    )}
                />

                <FormSelect
                    label="Duration to complete the program"
                    name="ProgramDuration"
                    options={PROGRAM_DURATION}
                />
                {values.ProgramDuration === 'morethan20hours' && (
                    <FormTextField
                        label="Please specify program duration (in hours)."
                        type="number"
                        name="OtherDurationHours"
                        onBlur={(e) => handleValueCheck(e.target.value)}
                        error={durationError}
                        helperText={durationError && `Number must be greater than 20`}
                    />
                )}
                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 2}}
                    multiple
                    fullWidth={true}
                    name="ResourcesType"
                    options={Object.values(RESOURCE_TYPE)}
                    value={resourcesTypeInitialValues}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("ResourcesType")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Type of resources your program has (Select all that apply)"
                            InputLabelProps={{required: true}}
                        />
                    )}
                />
                {values.ResourcesType && (values.ResourcesType.includes('ResTypeOther') || (values.ResourcesType.length > 1 && values.ResourcesType.includes('ResTypeOther'))) ? (
                    <FormTextField
                        label="Please specify your type of resources."
                        name="ResourcesTypeOther"
                    />
                ) : null}
                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 3, marginBottom: 3,}}
                    multiple
                    fullWidth={true}
                    name="ResourcesFormat"
                    options={Object.values(RESOURCES_FORMAT)}
                    value={programResourcesFormatInitialValues}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("ResourcesFormat")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Format of resources your program uses to deliver learning (Select all that apply)"
                            InputLabelProps={{required: true}}
                        />
                    )}
                />
                {values.ResourcesFormat && (values.ResourcesFormat.includes('FormatOthers') || (values.ResourcesFormat.length > 1 && values.ResourcesFormat.includes('FormatOthers'))) ? (
                    <FormTextField
                        label="Please specify your format of resources."
                        name="ProgramFormatOther"
                    />
                ) : null}

                <FormTextField label="Link to program" name="Links"/>

                <Autocomplete
                    className={classes.textFieldRoot}
                    sx={{marginTop: 1}}
                    multiple
                    fullWidth={true}
                    name="Languages"
                    options={Object.values(LANGUAGES)}
                    value={languagesInitialValues}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, value) => option.value === value.value}
                    onChange={handleAutocompleteChange("Languages")}
                    disableCloseOnSelect
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth={true}
                            variant="outlined"
                            label="Languages program is available in (Select all that apply)"
                            InputLabelProps={{required: true}}
                        />
                    )}
                />

                <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel htmlFor="RefFramework">Is your program already aligned with existing
                        framework or certification?</InputLabel>
                    <Field
                        style={{backgroundColor: '#fff'}}
                        as={Select}
                        id="RefFramework"
                        value={values.RefFramework}
                        label="Is your program already aligned with existing framework or certification??"
                        name="RefFramework"
                    >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Field>
                </FormControl>
                {values.RefFramework === 'yes' && (
                    <FormTextField

                        label="Which framework(s) does your program align with?"
                        name="RefAlignment"
                        required={false}
                    />
                )}
                <FormSelect
                    label="How much does your program cost to access per user? "
                    name="ProgramPrice"
                    options={PROGRAM_COST}
                />

                {/*<Autocomplete*/}
                {/*    className={classes.textFieldRoot}*/}
                {/*    sx={{marginTop: 4}}*/}
                {/*    multiple*/}
                {/*    fullWidth={true}*/}
                {/*    name="Devices"*/}
                {/*    options={Object.values(DEVICES)}*/}
                {/*    value={devicesInitialValues}*/}
                {/*    getOptionLabel={(option) => option.label}*/}
                {/*    getOptionSelected={(option, value) => option.value === value.value}*/}
                {/*    onChange={(event, newValue) => {*/}
                {/*        // If "Any device" is selected, clear the selection of other options*/}
                {/*        if (newValue.some(option => option.value === "AnyDevice")) {*/}
                {/*            newValue = newValue.filter(option => option.value === "AnyDevice");*/}
                {/*        }*/}
                {/*        handleAutocompleteChange("Devices")(event, newValue);*/}
                {/*    }}*/}
                {/*    disableCloseOnSelect*/}
                {/*    renderInput={(params) => (*/}
                {/*        <TextField*/}
                {/*            {...params}*/}
                {/*            fullWidth={true}*/}
                {/*            variant="outlined"*/}
                {/*            label="Device(s) needed by your target audience to use the resources (Select all that apply)"*/}
                {/*            InputLabelProps={{required: true}}*/}
                {/*            // placeholder="Multiple Autocomplete"*/}
                {/*        />*/}
                {/*    )}*/}
                {/*/>*/}
                {/*{values.Devices && (values.Devices.includes('Others') || (values.Devices.length > 1 && values.Devices.includes('Others'))) ? (*/}
                {/*    <FormTextField*/}
                {/*        label="Please specify device(s) needed."*/}
                {/*        name="DeviceTypeOther"*/}
                {/*    />*/}
                {/*) : null}*/}
                {/*{values.ProgramVenue && (values.ProgramVenue.includes('Others') || (values.ProgramVenue.length > 1 && values.ProgramVenue.includes('Others'))) ? (*/}
                {/*    <FormTextField*/}
                {/*        label="Please specify venue."*/}
                {/*        name="ProgramVenueOther"*/}
                {/*    />*/}
                {/*) : null}*/}


            </div>
                <div className={classes.section}>
                    <Typography variant="h4" component="h1" gutterBottom className={classes.infoTitle}>
                        Program Structure Details
                    </Typography>
                    <Typography className={classes.instruction} variant="body1">
                        How many courses are in your program? A course may target different audiences. (Yes/No)
                    </Typography>
                    <Typography className={classes.instruction} variant="body1"><InfoIcon/>
                        For each course, provide the following:
                    </Typography>
                    <FormTextField
                        label="Course Name"
                        name="CourseName"
                        multiline={true}
                        maxRows={10}
                        helperText={<MaxLengthHelperText {...courseNameObjValidProps} />}
                        error={courseNameObjValidProps.error}
                        validate={() => courseNameObjValidProps.error_message}
                    />
                    <FormTextField
                        label="Course Description"
                        name="CourseDesc"
                        multiline={true}
                        maxRows={10}
                        helperText={<MaxLengthHelperText {...CourseDescObjValidProps} />}
                        error={CourseDescObjValidProps.error}
                        validate={() => CourseDescObjValidProps.error_message}
                    />
                    <FormTextField
                        label="Course Learning Objectives"
                        name="CourseLearningObj"
                        multiline={true}
                        maxRows={10}
                        helperText={<MaxLengthHelperText {...courseLoObjValidProps} />}
                        error={courseLoObjValidProps.error}
                        validate={() => courseLoObjValidProps.error_message}
                    />
                    <Autocomplete
                        className={classes.textFieldRoot}
                        sx={{marginTop: 2, marginBottom: 3}}
                        multiple
                        fullWidth={true}
                        name="CourseTarget"
                        options={Object.values(PROGRAM_TARGETS)}
                        getOptionLabel={(option) => option.label}
                        value={courseTargetAudienceInitialValues}
                        getOptionSelected={(option, value) => option.value === value.value}
                        onChange={handleAutocompleteChange("CourseTarget")}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth={true}
                                variant="outlined"
                                label="Target Audience Group"
                                InputLabelProps={ { required: true }}
                            />
                        )}
                    />
                    {values.CourseTarget && (values.CourseTarget.includes('Others') || (values.CourseTarget.length > 1 && values.CourseTarget.includes('Others'))) ? (
                        <FormTextField
                            label="Please specify target audience."
                            name="CourseTargetOther"
                        />
                    ) : null}

                    <Autocomplete
                        className={classes.textFieldRoot}
                        sx={{marginTop: 2}}
                        multiple
                        fullWidth={true}
                        name="CourseTargetAge"
                        options={Object.values(TARGET_AGE)}
                        value={CourseTargetAgeInitialValues}
                        getOptionLabel={(option) => option.label}
                        getOptionSelected={(option, value) => option.value === value.value}
                        onChange={handleAutocompleteChange("CourseTargetAge")}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth={true}
                                variant="outlined"
                                label="Target Audience Age"
                                InputLabelProps={{required: true}}
                            />
                        )}
                    />

                    <Autocomplete
                        className={classes.textFieldRoot}
                        sx={{marginTop: 2}}
                        multiple
                        fullWidth={true}
                        name="CourseResourcesType"
                        options={Object.values(RESOURCE_TYPE)}
                        value={courseResourcesTypeInitialValues}
                        getOptionLabel={(option) => option.label}
                        getOptionSelected={(option, value) => option.value === value.value}
                        onChange={handleAutocompleteChange("CourseResourcesType")}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth={true}
                                variant="outlined"
                                label="Type of resources the course has (Select all that apply) "
                                InputLabelProps={{required: true}}
                            />
                        )}
                    />
                    {values.CourseResourcesType && (values.CourseResourcesType.includes('Others') || (values.CourseResourcesType.length > 1 && values.CourseResourcesType.includes('Others'))) ? (
                        <FormTextField
                            label="Please specify your type of resources."
                            name="CourseResourcesTypeOther"
                        />
                    ) : null}
                    <Autocomplete
                        className={classes.textFieldRoot}
                        sx={{marginTop: 3, marginBottom: 1,}}
                        multiple
                        fullWidth={true}
                        name="CourseResourcesFormat"
                        options={Object.values(RESOURCES_FORMAT)}
                        value={courseResourcesFormatInitialValues}
                        getOptionLabel={(option) => option.label}
                        getOptionSelected={(option, value) => option.value === value.value}
                        onChange={handleAutocompleteChange("CourseResourcesFormat")}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth={true}
                                variant="outlined"
                                label="Format of your course uses to deliver learning (Select all that apply)"
                                InputLabelProps={{required: true}}
                            />
                        )}
                    />
                    {values.CourseResourcesFormat && (values.CourseResourcesFormat.includes('FormatOthers') || (values.CourseResourcesFormat.length > 1 && values.CourseResourcesFormat.includes('FormatOthers'))) ? (
                        <FormTextField
                            label="Please specify your format of resources."
                            name="CourseFormatOther"
                        />
                    ) : null}

                </div>
                <Button type="submit" variant="contained" className={classes.badgeButton} disabled={disableSubmit}>
                    Submit
                </Button>
            </Form>
        </>
    );

};

const DQProgramInfoView = ({data, actions, options}) => {
    const classes = useStyles();
    const {defaultInitialValues, emptyInitialValues} = data;
    const {submitAction} = actions;
    const {} = options;

    const handleSubmit = (values) => {
        submitAction(values);
    }

    return (
        <Container className={classes.programContainer}>
            <Formik initialValues={(process.env.REACT_APP_NAME === 'LOCAL') ? defaultInitialValues : emptyInitialValues}
                    onSubmit={handleSubmit}>
                {({setFieldValue}) => (
                    <FormComponent setFieldValue={setFieldValue}/>
                )}
            </Formik>
        </Container>
    );
};

export default DQProgramInfoView;